/**
 * What to do with the service's answer — the outcome table.
 *
 * The governing rule (D385, owner, 2026-10-31): "Shopify's checkout is not a valid fallback.
 * EVERYONE must go to Peak." A merchant's Shopify checkout is often closed or broken, so a shopper
 * sent there is lost. The script may leave a shopper to Shopify's checkout in exactly one case: a
 * person deliberately switched the store off — the config answered `enabled: false`, or the create
 * endpoint answered `409 { error: "disabled" }`. That is the `disabled` outcome, and it is the only
 * one `goNative` (`peak-checkout.ts`) is ever called for. The `data-peak-ignore` theme opt-out is
 * the other way past the script; it never gets as far as an outcome.
 *
 * Four outcomes:
 *
 *   redirect → our checkout.
 *   cart     → the cart page: an empty cart has nothing to pay for (D375).
 *   disabled → the store is off: Shopify's checkout.
 *   failed   → everything else. The shopper stays on the store with a Peak notice and a retry
 *              (`./notice.ts`), after one more automatic attempt when that is worth it
 *              (`isRepeatable`). One `console.warn('[peak-checkout] …')` and one fallback report
 *              carry the reason code, so a merchant's support ticket and the staff logs have
 *              something to quote.
 *
 * Status contract (`docs/CHECKOUT-CREATE.md` §9):
 *   200 { redirectUrl }              → redirect
 *   409 { error: "disabled" }        → disabled: the kill switch was flipped between config and
 *                                      create, or the script could not read the config (D375)
 *   422 { error: "cart_invalid" }    → failed
 *   429                              → failed (retried once first when `Retry-After` ≤ 5 s)
 *   403 { error: "origin_not_allowed" }, …  → failed
 *   anything else                    → failed, http_{status}
 */

/** Where the native checkout lives. Relative on purpose: it is always same-origin. */
export const PEAK_NATIVE_CHECKOUT_PATH = '/checkout';

export type YcOutcome =
  | { kind: 'redirect'; url: string }
  /** `url` is the store's cart page. */
  | { kind: 'cart'; reason: string; url: string; status?: never }
  /**
   * `status` is the create endpoint's HTTP status when the server decided (D247), or that of the
   * failed cart read (D385).
   */
  | { kind: 'disabled'; reason: string; status?: number }
  | { kind: 'failed'; reason: string; status?: number | undefined };

/**
 * Only `http:` and `https:` absolute URLs, or root-relative paths, may be navigated to.
 *
 * The value comes from our own service, so this is defence in depth rather than a live threat:
 * it makes a `javascript:` or `data:` URL unreachable even if the service is ever made to echo
 * one back. A protocol-relative `//evil.example` is rejected too — it parses as a different
 * origin while looking relative.
 */
export function isSafeRedirectUrl(value: unknown): value is string {
  if (typeof value !== 'string' || value.length === 0) return false;
  if (value.slice(0, 2) === '//') return false;
  if (value.charAt(0) === '/') return true;

  const lower = value.toLowerCase();
  return lower.indexOf('https://') === 0 || lower.indexOf('http://') === 0;
}

/** Map a create-checkout response onto an outcome. */
export function decideOutcome(status: number, body: unknown): YcOutcome {
  if (status === 200) {
    const redirectUrl =
      typeof body === 'object' && body !== null
        ? (body as Record<string, unknown>).redirectUrl
        : undefined;
    if (isSafeRedirectUrl(redirectUrl)) return { kind: 'redirect', url: redirectUrl };
    return { kind: 'failed', reason: 'no_redirect_url', status };
  }

  const error =
    typeof body === 'object' && body !== null ? (body as Record<string, unknown>).error : undefined;
  if (status === 409 && error === 'disabled') return { kind: 'disabled', reason: error, status };
  if (typeof error === 'string' && /^[a-z0-9_]{1,40}$/.test(error)) {
    return { kind: 'failed', reason: error, status };
  }

  return { kind: 'failed', reason: 'http_' + String(status), status };
}

/**
 * Is this outcome worth one more automatic attempt of the whole handoff before the notice (D385)?
 * A failure with no answer (a network error, a timeout) or a server error may be gone a second
 * later. A 4xx is a considered refusal, the service's or Shopify's, and a 429 asks for the opposite
 * of a repeat, so those go straight to the notice.
 */
export function isRepeatable(outcome: YcOutcome): boolean {
  if (outcome.kind !== 'failed') return false;
  return outcome.status === undefined || outcome.status < 400 || outcome.status > 499;
}
